/** Pure transform editing over a cloned LevelDef. No DOM, Three.js, or physics. */

import type { LevelDef, Vec3Tuple } from '@/levels/schema';

export type EditorTargetKind =
  | 'geometry'
  | 'prop'
  | 'catBed'
  | 'grannySpawn'
  | 'routine'
  | 'hideSpot'
  | 'catMoment'
  | 'key'
  | 'dropZone';

export type EditorAxis = 'x' | 'y' | 'z';

export interface EditorTarget {
  key: string;
  kind: EditorTargetKind;
  id: string;
  label: string;
  position: Vec3Tuple;
  /** Radians, matching the level schema. Present only for rendered boxes/props. */
  rotationY?: number;
  canRotate: boolean;
}

export interface EditorTransform {
  position: Vec3Tuple;
  rotationY?: number;
}

export function cloneLevelForEditing(level: LevelDef): LevelDef {
  return structuredClone(level);
}

export function listEditorTargets(level: LevelDef): EditorTarget[] {
  const targets: EditorTarget[] = [];

  level.geometry.forEach((geometry) => {
    targets.push({
      key: `geometry:${geometry.id}`,
      kind: 'geometry',
      id: geometry.id,
      label: `Geometry · ${geometry.id}`,
      position: copyPosition(geometry.position),
      rotationY: geometry.rotationY ?? 0,
      canRotate: true,
    });
  });

  level.props.forEach((prop) => {
    targets.push({
      key: `prop:${prop.id}`,
      kind: 'prop',
      id: prop.id,
      label: `Prop · ${prop.id}`,
      position: copyPosition(prop.position),
      rotationY: prop.rotationY ?? 0,
      canRotate: true,
    });
  });

  targets.push({
    key: 'catBed',
    kind: 'catBed',
    id: 'catBed',
    label: 'Spawn · cat bed',
    position: copyPosition(level.catBed),
    canRotate: false,
  });
  targets.push({
    key: 'grannySpawn',
    kind: 'grannySpawn',
    id: 'grannySpawn',
    label: 'Spawn · granny',
    position: copyPosition(level.granny.spawn),
    canRotate: false,
  });

  level.granny.routine.forEach((activity, index) => {
    targets.push({
      key: `routine:${index}`,
      kind: 'routine',
      id: `${activity.activityId}-${index + 1}`,
      label: `Routine · ${activity.activityId} ${index + 1}`,
      position: copyPosition(activity.anchor),
      canRotate: false,
    });
  });

  level.hideSpots.forEach((spot) => {
    targets.push({
      key: `hideSpot:${spot.id}`,
      kind: 'hideSpot',
      id: spot.id,
      label: `Hide spot · ${spot.id}`,
      position: copyPosition(spot.position),
      canRotate: false,
    });
  });

  level.catMoments.forEach((moment) => {
    targets.push({
      key: `catMoment:${moment.id}`,
      kind: 'catMoment',
      id: moment.id,
      label: `Cat Moment · ${moment.id}`,
      position: copyPosition(moment.position),
      canRotate: false,
    });
  });

  level.keys.forEach((key) => {
    if (!key.position) return;
    targets.push({
      key: `key:${key.id}`,
      kind: 'key',
      id: key.id,
      label: `Key · ${key.id}`,
      position: copyPosition(key.position),
      canRotate: false,
    });
  });

  level.objectives.forEach((objective) => {
    if (!objective.dropZone) return;
    targets.push({
      key: `dropZone:${objective.id}`,
      kind: 'dropZone',
      id: objective.id,
      label: `Goal · ${objective.id}`,
      position: copyPosition(objective.dropZone.position),
      canRotate: false,
    });
  });

  return targets;
}

export function readEditorTarget(level: LevelDef, key: string): EditorTarget | null {
  return listEditorTargets(level).find((target) => target.key === key) ?? null;
}

/** Mutates the editor's private draft and returns the updated read-only target. */
export function setEditorTransform(
  level: LevelDef,
  key: string,
  transform: EditorTransform,
): EditorTarget | null {
  if (!transform.position.every(Number.isFinite)) return null;
  if (transform.rotationY !== undefined && !Number.isFinite(transform.rotationY)) return null;
  const ref = resolveTarget(level, key);
  if (!ref) return null;

  ref.position[0] = transform.position[0];
  ref.position[1] = transform.position[1];
  ref.position[2] = transform.position[2];
  if (ref.rotatable && transform.rotationY !== undefined && Number.isFinite(transform.rotationY)) {
    ref.rotatable.rotationY = transform.rotationY;
  }
  return readEditorTarget(level, key);
}

export function nudgeEditorTarget(
  level: LevelDef,
  key: string,
  axis: EditorAxis,
  distance: number,
): EditorTarget | null {
  if (!Number.isFinite(distance)) return null;
  const target = readEditorTarget(level, key);
  if (!target) return null;
  const position = copyPosition(target.position);
  position[axisIndex(axis)] += distance;
  return setEditorTransform(level, key, {
    position,
    ...(target.rotationY === undefined ? {} : { rotationY: target.rotationY }),
  });
}

export function rotateEditorTarget(
  level: LevelDef,
  key: string,
  radians: number,
): EditorTarget | null {
  const target = readEditorTarget(level, key);
  if (!target?.canRotate || target.rotationY === undefined || !Number.isFinite(radians))
    return null;
  return setEditorTransform(level, key, {
    position: target.position,
    rotationY: target.rotationY + radians,
  });
}

interface TargetRef {
  position: Vec3Tuple;
  rotatable?: { rotationY?: number };
}

function resolveTarget(level: LevelDef, key: string): TargetRef | null {
  if (key === 'catBed') return { position: level.catBed };
  if (key === 'grannySpawn') return { position: level.granny.spawn };

  const separator = key.indexOf(':');
  if (separator < 0) return null;
  const kind = key.slice(0, separator) as EditorTargetKind;
  const id = key.slice(separator + 1);

  if (kind === 'geometry') {
    const item = level.geometry.find((geometry) => geometry.id === id);
    return item ? { position: item.position, rotatable: item } : null;
  }
  if (kind === 'prop') {
    const item = level.props.find((prop) => prop.id === id);
    return item ? { position: item.position, rotatable: item } : null;
  }
  if (kind === 'routine') {
    const index = Number(id);
    const item = Number.isInteger(index) ? level.granny.routine[index] : undefined;
    return item ? { position: item.anchor } : null;
  }
  if (kind === 'hideSpot') {
    const item = level.hideSpots.find((spot) => spot.id === id);
    return item ? { position: item.position } : null;
  }
  if (kind === 'catMoment') {
    const item = level.catMoments.find((moment) => moment.id === id);
    return item ? { position: item.position } : null;
  }
  if (kind === 'key') {
    const item = level.keys.find((candidate) => candidate.id === id);
    return item?.position ? { position: item.position } : null;
  }
  if (kind === 'dropZone') {
    const item = level.objectives.find((objective) => objective.id === id);
    return item?.dropZone ? { position: item.dropZone.position } : null;
  }
  return null;
}

function copyPosition(position: Vec3Tuple): Vec3Tuple {
  return [position[0], position[1], position[2]];
}

function axisIndex(axis: EditorAxis): 0 | 1 | 2 {
  if (axis === 'x') return 0;
  if (axis === 'y') return 1;
  return 2;
}
